import { createTheme } from '@mui/material/styles';

// TMT/Upshop Brand Colors - TEAL (matching Blazor app)
const brandTeal = '#006261';
const brandTealDark = '#004D4C';
const brandTealLight = '#4DB6AC';

// Sidebar colors
const sidebarBg = '#006261';
const sidebarHeaderBg = '#004D4C';
const sidebarText = 'rgba(255, 255, 255, 0.88)';
const sidebarTextMuted = 'rgba(255, 255, 255, 0.55)';
const sidebarHoverBg = 'rgba(255, 255, 255, 0.08)';
const sidebarActiveBg = 'rgba(255, 255, 255, 0.18)';
const sidebarBorder = '#007A79';

// Background colors
const background = '#F2F5F4';
const cardBg = '#FFFFFF';

// Text colors
const foreground = '#1E2A3B';
const foregroundMuted = '#6B7280';

export const theme = createTheme({
  palette: {
    primary: {
      main: brandTeal,
      dark: brandTealDark,
      light: brandTealLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#455a64',
      dark: '#1c313a',
      light: '#718792',
    },
    background: {
      default: background,
      paper: cardBg,
    },
    text: {
      primary: foreground,
      secondary: foregroundMuted,
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#FF9800',
    },
    success: {
      main: '#4CAF50',
    },
    info: {
      main: '#2196F3',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.8125rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '6px 12px',
          fontSize: '0.8125rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: '#D1D5DB',
          color: foreground,
          '&:hover': {
            backgroundColor: '#F9FAFB',
            borderColor: '#9CA3AF',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E5E7EB',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: 'hidden',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          fontSize: '0.8125rem',
          borderBottom: '1px solid #F3F4F6',
        },
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: '#F9FAFB',
          color: foreground,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 500,
          height: '24px',
          borderRadius: 6,
        },
        sizeSmall: {
          height: '22px',
          fontSize: '0.6875rem',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: sidebarBg,
          color: sidebarText,
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 8px',
          padding: '10px 12px',
          color: sidebarText,
          '&:hover': {
            backgroundColor: sidebarHoverBg,
            color: '#FFFFFF',
          },
          '&.Mui-selected': {
            backgroundColor: sidebarActiveBg,
            color: '#FFFFFF',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
            },
            // Left border indicator
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 3,
              backgroundColor: brandTealLight,
              borderRadius: '0 2px 2px 0',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: sidebarText,
          minWidth: 40,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: sidebarHeaderBg,
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            fontSize: '0.8125rem',
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: brandTeal,
          textDecoration: 'none',
          '&:hover': {
            color: brandTealDark,
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

// Export sidebar colors for use in Layout component
export const sidebarColors = {
  bg: sidebarBg,
  headerBg: sidebarHeaderBg,
  text: sidebarText,
  textMuted: sidebarTextMuted,
  hoverBg: sidebarHoverBg,
  activeBg: sidebarActiveBg,
  border: sidebarBorder,
  activeIndicator: brandTealLight,
};
